import { Box } from "@mui/material";
import * as React from "react";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";
import { ConfirmationDialogResult, DatabaseRecordType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import * as store from "../../../framework/customStore";
import { SaveDataObj } from "../../../framework/saveData";
import * as messageService from "../../../models/services/messageService";
import { ISessionState } from "../../../models/session/sessionState";
import * as storeActions from "../../../models/store/storeActions";
import { CkEditorDialog } from "../../framework/Dialog/Dialog";
import { DialogItemsNav } from "../../framework/Dialog/DialogItemsNav";
import { DialogTabs } from "../../framework/Dialog/DialogTabs";
import { IModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { LockedRecord } from "ui/Scripts/source/models/common/lockedRecord";
import { recordLockManager } from "source/models/services/recordLockService";
import { StoreUtils } from "source/models/store/storeUtils";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { removeConfirmationWord } from "ui/Scripts/source/models/consts";
import { Base } from "ui/Scripts/source/framework/base";
import { IMessageEditItem } from "source/models/message/messageEditItem";
import { IMessageTypeListItem } from "source/models/messageType/messageTypeListItem";
import { IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { MessageEditDialogGeneralInformation } from "./MessageEditDialogGeneralInformation";
import { IRecordUnit, RecordUnitSave } from "ui/Scripts/source/models/unit/recordUnit";
import { IMessageEditItemTargetCompanyGroup, MessageEditItemTargetCompanyGroupSave } from "source/models/message/messageEditItemTargetCompanyGroup";
import { MessageEditDialogAttachments } from "./MessageEditDialogAttachments";
import { ReadReceiptList } from "source/components/readReceipt/readReceiptList";
import { MessageSendEmailList } from "../MessageSendEmailList";
import { SaveAttachmentBase64 } from "ui/Scripts/source/models/common/saveAttachmentBase64";
import { AppButton } from "ui/Scripts/source/components/framework/Button/AppButton";

/* eslint-disable no-unused-vars */
export const enum MessageEditDialogMenuItem {
    General = "1",
    Attachments = "2",
    ReadReceipts = "3",
    SentEmails = "4"
}
/* eslint-enable no-unused-vars */

export interface IMessageEditDialogProp {
    sessionState: ISessionState;
    messageCompanyId: string;
    messageId: string;
    forwardMessage: boolean;
    hasPrevious: boolean;
    hasNext: boolean;
    onOk: () => void;
    onClose: () => void;
    onRefreshList: () => Promise<void>;
    onPrevious: () => void;
    onNext: () => void;
}

export interface IMessageEditDialogState {
    activeMenuItem: MessageEditDialogMenuItem;
    isLoading: boolean;
    hasNormalCompanyGroups: boolean;
    hasFixedCompanyGroups: boolean;
    canUseAllCompanyGroup: boolean;
    canSaveAndSendEmail: boolean;
    message: IMessageEditItem;
    messageCompanyId: string;
    id: string;
    rowId: string;
    title: string;
    content: string;
    contentDirty: boolean;
    messageTypeId: string;
    dateStr: string;
    startDateStr: string;
    endDateStr: string;
    unitId: string;
    requiresReceipt: boolean;
    systemAdminMessage: boolean;
    messageTargetUnits: IRecordUnit[];
    messageTargetCompanyGroups: IMessageEditItemTargetCompanyGroup[];
    attachments: IFileAttachment[];
    messageTypes: IMessageTypeListItem[];
    senderUnits: IIdTitleNode[];
    targetUnits: IIdTitleNode[];
    lockedUser: IModifyUser;
    isLockedToOtherUser: boolean;
}

export class MessageEditDialog extends React.Component<IMessageEditDialogProp, IMessageEditDialogState> {
    private _isMounted: boolean;
    private orgStateHash: string = "";

    constructor(props: IMessageEditDialogProp) {
        super(props);
        this.state = {
            activeMenuItem: MessageEditDialogMenuItem.General,
            isLoading: false,
            hasNormalCompanyGroups: false,
            hasFixedCompanyGroups: false,
            canUseAllCompanyGroup: false,
            canSaveAndSendEmail: false,
            message: null,
            messageCompanyId: props.messageCompanyId,
            id: props.messageId,
            rowId: "",
            title: "",
            content: "",
            contentDirty: false,
            messageTypeId: "",
            dateStr: "",
            startDateStr: "",
            endDateStr: "",
            unitId: "",
            requiresReceipt: false,
            systemAdminMessage: false,
            messageTargetUnits: [],
            messageTargetCompanyGroups: [],
            attachments: [],
            messageTypes: [],
            senderUnits: [],
            targetUnits: [],
            lockedUser: null,
            isLockedToOtherUser: false,
        };
    }

    loadData = async (companyId: string, id: string, forwardMessage: boolean = false) => {
        const props = this.props;
        //Unlock current message
        if (LockedRecord.isLockedToUser(this.state.lockedUser, props.sessionState.code)) {
            await recordLockManager.unlock(this.state.messageCompanyId, this.state.id, DatabaseRecordType.Message);
        }
        //Load and lock message
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => messageService.getMessageEdit(companyId, id, forwardMessage), false, false, () => this.setState({ isLoading: false }));
        if (!result) return;
        const message = result.item;
        const isNew = message.id === Base.emptyGuid;
        const state = {
            ...this.state,
            hasNormalCompanyGroups: result.hasNormalCompanyGroups,
            hasFixedCompanyGroups: result.hasFixedCompanyGroups,
            canUseAllCompanyGroup: result.canUseAllCompanyGroup,
            canSaveAndSendEmail: result.canSaveAndSendEmail,
            message,
            id: message.id,
            messageCompanyId: message.companyId,
            rowId: message.rowId,
            title: message.title,
            content: message.content,
            messageTypeId: message.messageTypeId,
            dateStr: Base.utcTimeToDateStr(message.date),
            startDateStr: Base.utcTimeToDateStr(message.startDate),
            endDateStr: message.endDate ? Base.utcTimeToDateStr(message.endDate) : "",
            unitId: message.unitId,
            requiresReceipt: message.requiresReceipt,
            systemAdminMessage: message.systemAdminMessage,
            messageTargetUnits: message.targetUnits,
            messageTargetCompanyGroups: message.targetCompanyGroups,
            attachments: message.attachments,
            messageTypes: result.messageTypes,
            senderUnits: result.senderUnits,
            targetUnits: result.targetUnits,
            lockedUser: message.lockedUser,
            isLockedToOtherUser: message.isLockedToOtherUser(props.sessionState.code)
        };
        this.setState(state);
        this.orgStateHash = await this.getHashFromState(props, state);
        //Add locked record to idb
        if (!isNew && LockedRecord.isLockedToUser(message.lockedUser, props.sessionState.code)) {
            recordLockManager.addLock(LockedRecord.createLockedRecord(state.messageCompanyId, state.id, props.sessionState.code, DatabaseRecordType.Message));
        }
        //Send read receipt for message = NOT NEEDED EDIT DIALOG IS ONLY USABLE FOR EDITORS
        // if (!isNew) {
        //     const userIsEditor = message.modifyUser.code === props.sessionState.code;
        //     if (!userIsEditor && !message.isRead) {
        //         AppUtils.callService(() => messageService.sendReadReceipt(state.id, message.companyId, ReadReceiptCategory.Automatic), false, true);
        //     }
        // }
    };

    componentDidMount(): void {
        this._isMounted = true;
        const props = this.props;
        this.loadData(props.messageCompanyId, props.messageId, props.forwardMessage);
    }

    async componentWillUnmount(): Promise<void> {
        this._isMounted = false;
        //Unlock on close
        const props = this.props;
        const state = this.state;
        const isNew = state.id === Base.emptyGuid;
        if (!isNew && LockedRecord.isLockedToUser(this.state.lockedUser, props.sessionState.code)) {
            await recordLockManager.unlock(state.messageCompanyId, state.id, DatabaseRecordType.Message);
        }
    }

    async componentDidUpdate(prevProps: IMessageEditDialogProp, prevState: IMessageEditDialogState): Promise<void> {
        const props = this.props;
        const state = this.state;
        if (prevProps.messageCompanyId === props.messageCompanyId && prevProps.messageId === props.messageId) {
            return;
        }
        const cancelResult = await AppUtils.cancel(await this.getHashFromState(props, state), this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            if (!await this.saveToDb(false)) return;
            props.onRefreshList();
        }
        await this.loadData(props.messageCompanyId, props.messageId);
    }

    handleToggleActiveMenuItem = (activeMenuItem: MessageEditDialogMenuItem) => {
        this.setState({ activeMenuItem });
    };

    handleChangeSystemAdminMessage = (systemAdminMessage: boolean) => {
        const state = this.state;
        this.setState({
            systemAdminMessage,
            messageTargetUnits: systemAdminMessage ? [] : state.messageTargetUnits,
            messageTargetCompanyGroups: systemAdminMessage ? [] : state.messageTargetCompanyGroups
        });
    };

    // #region Locking
    handleUnlockAndLockToMe = async () => {
        const props = this.props;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToUnlockMessage, this.state.message.title), false) !== ConfirmationDialogResult.Yes) {
            return;
        }
        const state = this.state;
        const result = await recordLockManager.lock(state.messageCompanyId, state.id, props.sessionState.code, DatabaseRecordType.Message);
        if (!result || result.items.length < 1) return;
        this.setState({
            lockedUser: StoreUtils.getNowModifyUser(result.items[0].lockedDate),
            isLockedToOtherUser: false
        });
    };
    // #endregion Locking

    getBaseSaveDataFromState = async (props: IMessageEditDialogProp, state: IMessageEditDialogState, sendEmail: boolean = false): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        result.append("rowId", state.rowId);
        result.append("companyId", state.messageCompanyId);
        result.append("id", state.id);
        result.append("title", state.title);
        result.append("messageTypeId", state.messageTypeId);
        result.append("unitId", state.unitId);
        result.append("date", state.dateStr.toUtcDate().getTime());
        result.append("startDate", state.startDateStr.toUtcDate().getTime());
        result.append("endDate", state.endDateStr ? state.endDateStr.toUtcDate().getTime() : null);
        result.append("systemAdminMessage", state.systemAdminMessage);
        result.append("requiresReceipt", state.requiresReceipt);
        result.append("targetUnits", state.messageTargetUnits.map(i => new RecordUnitSave(i)));
        result.append("targetCompanyGroups", state.messageTargetCompanyGroups.map(i => new MessageEditItemTargetCompanyGroupSave(i)));
        result.append("sendEmail", sendEmail);
        if (state.message.isNew()) {
            result.append("attachments", await SaveAttachmentBase64.fromFileAttachments(state.attachments));
        }
        return result;
    };

    getSaveDataFromState = async (props: IMessageEditDialogProp, state: IMessageEditDialogState, sendEmail: boolean = false): Promise<SaveDataObj> => {
        const result = await this.getBaseSaveDataFromState(props, state, sendEmail);
        result.append("content", state.content);
        return result;
    };

    getHashFromState = async (props: IMessageEditDialogProp, state: IMessageEditDialogState): Promise<string> => {
        const result = await this.getBaseSaveDataFromState(props, state, false);
        result.append("contentDirty", state.contentDirty);
        return result.hash;
    };
    
    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.title) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.TitleMustBeDefined));
                return resolve(false);
            }
            if (!state.dateStr) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.DateMustBeDefined));
                return resolve(false);
            }
            if (!state.startDateStr) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.MessageStartDateMustBeDefined));
                return resolve(false);
            }
            return resolve(true);
        });
    };

    saveToDb = async (sendEmail: boolean): Promise<SaveSuccess> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        const saveData = await this.getSaveDataFromState(props, state, sendEmail);
        //Call server
        const result = await AppUtils.callService(() => messageService.saveMessageEdit(saveData.obj));
        if (!result) return null;
        //Update state and hash
        const stateChange = { id: result.id, rowId: result.rowId, contentDirty: false };
        this.orgStateHash = await this.getHashFromState(props, { ...state, ...stateChange });
        this.setState(stateChange);
        return result;
    };

    handleSave = async (closeDialog: boolean) => {
        const result = await this.saveToDb(false);
        if (!result) return;
        if (!closeDialog) {
            this.loadData(this.state.messageCompanyId, result.id);
            this.props.onOk();
            return;
        }
        this.props.onClose();
    };

    handleSaveAndSendEmail = async () => {
        const props = this.props;
        const state = this.state;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToEmailMessageParameter, state.title)) === ConfirmationDialogResult.Yes) {
            const result = await this.saveToDb(true);
            if (!result) return;
            this.loadData(state.messageCompanyId, result.id);
            props.onOk();
        }
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (state.isLockedToOtherUser || state.message.isNew()) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveMessageParameter, state.message.title), false, removeConfirmationWord) === ConfirmationDialogResult.Yes) {
            const result = await AppUtils.callService(() => messageService.removeMessage(state.messageCompanyId, state.id));
            if (!result) return;
            this.setState({ lockedUser: null }, props.onClose);
        }
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(await this.getHashFromState(props, state), this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onClose();
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        return await AppUtils.canMoveOut(await this.getHashFromState(props, this.state), this.orgStateHash,
            async () => !!await this.saveToDb(false),
            props.onRefreshList,
            (newStateHash: string) => { this.orgStateHash = newStateHash; });
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.message) return null;
        const isNew = state.message.isNew();
        return (
            <CkEditorDialog
                id="messageEditDialog"
                maxWidth="xl"
                maxHeight="100%"
                title={isNew ? Translations.AddMessage : (Translations.EditMessage + ": " + state.message.title + (state.isLockedToOtherUser ? ", " + Translations.Locked : ""))}
                dialogTabs={
                    <DialogTabs
                        activeTabId={state.activeMenuItem}
                        tabs={[
                            { id: MessageEditDialogMenuItem.General, title: Translations.BasicInformation },
                            { id: MessageEditDialogMenuItem.Attachments, title: Translations.Attachments, badgeContent: state.attachments.length },
                            { id: MessageEditDialogMenuItem.ReadReceipts, title: Translations.ReadReceipts, hidden: isNew },
                            { id: MessageEditDialogMenuItem.SentEmails, title: Translations.SentEmails, hidden: isNew },
                        ]}
                        onChangeTab={this.handleToggleActiveMenuItem}
                    />
                }
                body={
                    <>
                        {state.activeMenuItem === MessageEditDialogMenuItem.General &&
                            <MessageEditDialogGeneralInformation
                                sessionState={props.sessionState}
                                hasNormalCompanyGroups={state.hasNormalCompanyGroups}
                                hasFixedCompanyGroups={state.hasFixedCompanyGroups}
                                canUseAllCompanyGroup={state.canUseAllCompanyGroup}
                                isNew={isNew}
                                companyId={state.messageCompanyId}
                                messageId={state.id}
                                title={state.title}
                                content={state.content}
                                messageTypeId={state.messageTypeId}
                                dateStr={state.dateStr}
                                startDateStr={state.startDateStr}
                                endDateStr={state.endDateStr}
                                unitId={state.unitId}
                                requiresReceipt={state.requiresReceipt}
                                systemAdminMessage={state.systemAdminMessage}
                                messageTargetUnits={state.messageTargetUnits}
                                messageTargetCompanyGroups={state.messageTargetCompanyGroups}
                                messageTypes={state.messageTypes}
                                senderUnits={state.senderUnits}
                                targetUnits={state.targetUnits}
                                isLockedToOtherUser={state.isLockedToOtherUser}
                                onChangeTitle={(title: string) => this.setState({ title })}
                                onChangeDateStr={(dateStr: string) => this.setState({ dateStr })}
                                onChangeStartDateStr={(startDateStr: string) => this.setState({ startDateStr })}
                                onChangeEndDateStr={(endDateStr: string) => this.setState({ endDateStr })}
                                onChangeMessageType={(messageTypeId: string) => this.setState({ messageTypeId })}
                                onChangeUnit={(unitId: string) => this.setState({ unitId })}
                                onChangeRequiresReceipt={(requiresReceipt: boolean) => this.setState({ requiresReceipt })}
                                onChangeSystemAdminMessage={this.handleChangeSystemAdminMessage}
                                onChangeContent={(content: string, contentDirty: boolean) => this.setState({ content, contentDirty })}
                                onChangeMessageTargetUnits={(messageTargetUnits: IRecordUnit[]) => this.setState({ messageTargetUnits })}
                                onChangeMessageTargetCompanyGroups={(messageTargetCompanyGroups: IMessageEditItemTargetCompanyGroup[]) => this.setState({ messageTargetCompanyGroups })}
                            />
                        }
                        {state.activeMenuItem === MessageEditDialogMenuItem.Attachments &&
                            <MessageEditDialogAttachments
                                sessionState={props.sessionState}
                                isLockedToOtherUser={state.isLockedToOtherUser}
                                isNew={isNew}
                                companyId={state.messageCompanyId}
                                messageId={state.id}
                                attachments={state.attachments}
                                onChangeAttachments={(attachments: IFileAttachment[]) => this.setState({ attachments })}
                            />
                        }
                        {state.activeMenuItem === MessageEditDialogMenuItem.ReadReceipts &&
                            <ReadReceiptList
                                companyId={state.messageCompanyId}
                                recordId={state.id}
                                height="100%"
                                getReadReceiptListItems={messageService.getMessageReadReceiptListItems}
                            />
                        }
                        {state.activeMenuItem === MessageEditDialogMenuItem.SentEmails &&
                            <MessageSendEmailList
                                companyId={state.messageCompanyId}
                                messageId={state.id}
                                height="100%"
                            />
                        }
                    </>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={this.handlePrevious}
                            onNext={this.handleNext}
                            modifyUser={!isNew ? state.message.modifyUser : null}
                            lockedUser={state.lockedUser}
                            isLockedToOtherUser={!isNew && state.isLockedToOtherUser}
                            onUnlockAndLockToMe={props.sessionState.isSuperAdmin ? this.handleUnlockAndLockToMe : null}
                        />
                        <DialogButtons
                            isNew={isNew}
                            removeDisabled={state.isLockedToOtherUser}
                            saveDisabled={state.isLockedToOtherUser}
                            prefixNode={<AppButton disabled={!state.canSaveAndSendEmail || state.systemAdminMessage} variant="contained" onClick={this.handleSaveAndSendEmail} sx={{ marginRight: "1.5rem" }}>{Translations.SaveAndEmail}</AppButton>}
                            onCancel={this.handleCancel}
                            onRemove={!isNew ? this.handleRemove : null}
                            onSave={() => this.handleSave(false)}
                            
                        />
                    </Box>
                }
                onClose={this.handleCancel}/>
        );
    }
}